/* eslint-disable max-len */
import { IConfig, IAppConfig, IEnvironmentSpecificConfig, IConfigExtras } from './common';
import { Environment, Environments } from './environments';
import {
    getEnvironment as mobileEnvironment,
    getConfig as getMobileConfig
} from './mobile-config';
import { getAuthConfig, isEDN } from './auth-config';
import { Platform } from './platform';
import { isAuthExemptedForEnvironment } from './auth-exempted-env-config';
import getHostUrl from './get-host';

declare let global: {
    env: string;
    version: string;
    mockApiVersion?: string;
};

const mockApiVersionDev = 'developer';

const getAppConfig = (): IAppConfig => {
    const baseConfig = {
        vendorId: '0b6553',
        platform: Platform.kind,
        deviceType: Platform.deviceType,
        mockApiVersion: mockApiVersionDev
    };

    if (Platform.isDesktop) {
        return {
            ...baseConfig,
            version: global.version,
            appVersion: global.version,
            ...(global.mockApiVersion && { mockApiVersion: global.mockApiVersion })
        };
    }

    const config = getMobileConfig();
    return {
        ...baseConfig,
        version: config.version,
        appVersion: config.appVersion,
        ...(config.mockApiVersion && { mockApiVersion: config.mockApiVersion })
    };
};

// tslint:disable: max-line-length
const Configurations: { [key: string]: IEnvironmentSpecificConfig } = {
    [Environments.Production]: {
        isForDevelopment: false,
        apiHostUrl: getHostUrl('aHR0cHM6Ly90YXBpLnRlbHN0cmEuY29tL3ByZXNlbnRhdGlvbi92MS90ZWxzdHJhLWNvbm5lY3Q='), // 'https://tapi.telstra.com/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkudGVsc3RyYS5jb20vdGVsc3RyYS1jb25uZWN0L3ByZXNlbnRhdGlvbi92MS90ZWxzdHJhLWNvbm5lY3Q='), // https://prod.okapi.ogw.evolve.okapi.telstra.com/telstra-connect/presentation/v1/telstra-connect
        appHostUrl: getHostUrl('aHR0cHM6Ly9jb25uZWN0YXBwLnRlbHN0cmEuY29t'), // 'https://connectapp.telstra.com',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.PreProduction]: {
        isForDevelopment: false,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9zdGFnaW5nLXRhcGkudGVsc3RyYS5jb20vcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // 'https://staging-tapi.telstra.com/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkudGVsc3RyYS5jb20vdGVsc3RyYS1jb25uZWN0LXN0YWdpbmcvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // https://prod.okapi.ogw.evolve.okapi.telstra.com/telstra-connect-staging/presentation/v1/telstra-connect
        appHostUrl: getHostUrl('aHR0cHM6Ly9wcmVwcm9kLmNvbm5lY3RhcHAudGVsc3RyYS5jb20='), // 'https://preprod.connectapp.telstra.com',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.UAT]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9zbG90NS5vcmcwMDUudC1kZXYudGVsc3RyYS5uZXQvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // 'https://slot5.org005.t-dev.telstra.net/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9ub25wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkudGVsc3RyYS5jb20vdGVsc3RyYS1jb25uZWN0LXVhdC9wcmVzZW50YXRpb24vdjEvdGVsc3RyYS1jb25uZWN0'), // https://nonprod.okapi.ogw.evolve.okapi.telstra.com/telstra-connect-uat/presentation/v1/telstra-connect
        appHostUrl: getHostUrl('aHR0cHM6Ly91YXQuY29ubmVjdGFwcC50ZWxzdHJhLmNvbQ=='), // 'https://uat.connectapp.telstra.com',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.QA]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9xYS5vcmcwMDUudC1kZXYudGVsc3RyYS5uZXQvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // https://qa.org005.t-dev.telstra.net/presentation/v1/telstra-connect
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9ub25wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkudGVsc3RyYS5jb20vdGVsc3RyYS1jb25uZWN0LXFhL3ByZXNlbnRhdGlvbi92MS90ZWxzdHJhLWNvbm5lY3Q='), // https://nonprod.okapi.ogw.evolve.okapi.telstra.com/telstra-connect-qa/presentation/v1/telstra-connect
        appHostUrl: getHostUrl('aHR0cHM6Ly9xYS5jb25uZWN0YXBwLnRlbHN0cmEuY29t'), // 'https://qa.connectapp.telstra.com',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.SVT]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9xYS5vcmcwMDUudC1kZXYudGVsc3RyYS5uZXQvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdC1zdnQ='), // 'https://qa.org005.t-dev.telstra.net/presentation/v1/telstra-connect-svt',
        apiHostAuthExemptUrl: getHostUrl('aHR0cHM6Ly9zbG90NC5vcmcwMDUudC1kZXYudGVsc3RyYS5uZXQvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdC1zdnQ='), // 'https://slot4.org005.t-dev.telstra.net/presentation/v1/telstra-connect-svt',
        apiOGWHostUrl: getHostUrl(''),
        appHostUrl: getHostUrl('aHR0cHM6Ly9wb2MuY29ubmVjdGFwcC50ZWxzdHJhLmNvbQ=='), // 'https://poc.connectapp.telstra.com',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.Development]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9zbG90My5vcmcwMDUudC1kZXYudGVsc3RyYS5uZXQvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // 'https://slot3.org005.t-dev.telstra.net/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9ub25wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkudGVsc3RyYS5jb20vdGVsc3RyYS1jb25uZWN0LWRldi9wcmVzZW50YXRpb24vdjEvdGVsc3RyYS1jb25uZWN0'), // https://nonprod.okapi.ogw.evolve.okapi.telstra.com/telstra-connect-dev/presentation/v1/telstra-connect
        appHostUrl: getHostUrl('aHR0cHM6Ly9kZXYuY29ubmVjdGFwcC50ZWxzdHJhLmNvbQ=='), // 'https://dev.connectapp.telstra.com',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.Mock]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9tb2NrLWFwaS5jb25uZWN0YXBwLnRlbHN0cmEuY29tL2FwaS92LQ=='), // 'https://mock-api.connectapp.telstra.com/api/v-',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9tb2NrLWFwaS5jb25uZWN0YXBwLnRlbHN0cmEuY29tL2FwaS92LWRldmVsb3Blcg=='), // 'https://mock-api.connectapp.telstra.com/api/v-developer',
        appHostUrl: getHostUrl('aHR0cHM6Ly9tb2NrLmNvbm5lY3RhcHAudGVsc3RyYS5jb20='), // 'https://mock.connectapp.telstra.com',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.Local]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cDovL2xvY2FsaG9zdDo1MDUwL2FwaS92LQ=='), // 'http://localhost:5050/api/v-',
        apiOGWHostUrl: getHostUrl('aHR0cDovL2xvY2FsaG9zdDo1MDUwL2FwaS92LWRldmVsb3Blcg=='), // 'http://localhost:5050/api/v-developer',
        appHostUrl: getHostUrl('aHR0cDovL2xvY2FsaG9zdDo1MDAw'), // 'http://localhost:5000',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    }
};

const ConfigurationsEDN: { [key: string]: IEnvironmentSpecificConfig } = {
    [Environments.Production]: {
        isForDevelopment: false,
        apiHostUrl: getHostUrl('aHR0cHM6Ly90YXBpLnQtZGV2LmluLnRlbHN0cmEuY29tLmF1L3ByZXNlbnRhdGlvbi92MS90ZWxzdHJhLWNvbm5lY3Q='), // 'https://tapi.t-dev.in.telstra.com.au/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkuYXdzLmNsb3VkLmluLnRlbHN0cmEuY29tLmF1L3RlbHN0cmEtY29ubmVjdC9wcmVzZW50YXRpb24vdjEvdGVsc3RyYS1jb25uZWN0LXN0YWZm'), // https://prod.okapi.ogw.evolve.okapi.aws.cloud.in.telstra.com.au/telstra-connect/presentation/v1/telstra-connect-staff
        appHostUrl: getHostUrl('aHR0cHM6Ly9jb25uZWN0YXBwLmluLnRlbHN0cmEuY29tLmF1'), // 'https://connectapp.in.telstra.com.au',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.PreProduction]: {
        isForDevelopment: false,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9zdGFnaW5nLXRhcGkudC1kZXYuaW4udGVsc3RyYS5jb20uYXUvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // 'https://staging-tapi.t-dev.in.telstra.com.au/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkuYXdzLmNsb3VkLmluLnRlbHN0cmEuY29tLmF1L3RlbHN0cmEtY29ubmVjdC1zdGFnaW5nL3ByZXNlbnRhdGlvbi92MS90ZWxzdHJhLWNvbm5lY3Qtc3RhZmY='), // https://prod.okapi.ogw.evolve.okapi.aws.cloud.in.telstra.com.au/telstra-connect-staging/presentation/v1/telstra-connect-staff
        appHostUrl: getHostUrl('aHR0cHM6Ly9jb25uZWN0YXBwLnByZXByb2QuaW4udGVsc3RyYS5jb20uYXU='), // 'https://connectapp.preprod.in.telstra.com.au',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.UAT]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9zbG90NS5vcmcwMDUudC1kZXYuaW4udGVsc3RyYS5jb20uYXUvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // 'https://slot5.org005.t-dev.in.telstra.com.au/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9ub25wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkuYXdzLmNsb3VkLmluLnRlbHN0cmEuY29tLmF1L3RlbHN0cmEtY29ubmVjdC11YXQvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdC1zdGFmZg=='), // https://nonprod.okapi.ogw.evolve.okapi.aws.cloud.in.telstra.com.au/telstra-connect-uat/presentation/v1/telstra-connect-staff
        appHostUrl: getHostUrl('aHR0cHM6Ly91YXQuY29ubmVjdGFwcC5ucC5pbi50ZWxzdHJhLmNvbS5hdQ=='), // 'https://uat.connectapp.np.in.telstra.com.au',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.SVT]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9xYS5vcmcwMDUudC1kZXYudGVsc3RyYS5uZXQvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdC1zdnQ='), // 'https://qa.org005.t-dev.telstra.net/presentation/v1/telstra-connect-svt',
        apiOGWHostUrl: getHostUrl(''),
        apiHostAuthExemptUrl: getHostUrl('aHR0cHM6Ly9zbG90NC5vcmcwMDUudC1kZXYudGVsc3RyYS5uZXQvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdC1zdnQ='), // 'https://slot4.org005.t-dev.telstra.net/presentation/v1/telstra-connect-svt',
        appHostUrl: getHostUrl('aHR0cHM6Ly9wb2MuY29ubmVjdGFwcC5ucC5pbi50ZWxzdHJhLmNvbS5hdQ=='), // 'https://poc.connectapp.np.in.telstra.com.au',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.QA]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9xYS5vcmcwMDUudC1kZXYuaW4udGVsc3RyYS5jb20uYXUvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // 'https://qa.org005.t-dev.in.telstra.com.au/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9ub25wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkuYXdzLmNsb3VkLmluLnRlbHN0cmEuY29tLmF1L3RlbHN0cmEtY29ubmVjdC1xYS9wcmVzZW50YXRpb24vdjEvdGVsc3RyYS1jb25uZWN0LXN0YWZm'), // https://nonprod.okapi.ogw.evolve.okapi.aws.cloud.in.telstra.com.au/telstra-connect-qa/presentation/v1/telstra-connect-staff
        appHostUrl: getHostUrl('aHR0cHM6Ly9xYS5jb25uZWN0YXBwLm5wLmluLnRlbHN0cmEuY29tLmF1'), // 'https://qa.connectapp.np.in.telstra.com.au',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.Development]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9zbG90My5vcmcwMDUudC1kZXYuaW4udGVsc3RyYS5jb20uYXUvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdA=='), // 'https://slot3.org005.t-dev.in.telstra.com.au/presentation/v1/telstra-connect',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9ub25wcm9kLm9rYXBpLm9ndy5ldm9sdmUub2thcGkuYXdzLmNsb3VkLmluLnRlbHN0cmEuY29tLmF1L3RlbHN0cmEtY29ubmVjdC1kZXYvcHJlc2VudGF0aW9uL3YxL3RlbHN0cmEtY29ubmVjdC1zdGFmZg=='), // https://nonprod.okapi.ogw.evolve.okapi.aws.cloud.in.telstra.com.au/telstra-connect-dev/presentation/v1/telstra-connect-staff
        appHostUrl: getHostUrl('aHR0cHM6Ly9kZXYuY29ubmVjdGFwcC5ucC5pbi50ZWxzdHJhLmNvbS5hdQ=='), // 'https://dev.connectapp.np.in.telstra.com.au',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.Mock]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cHM6Ly9tb2NrLWFwaS5jb25uZWN0YXBwLnRlbHN0cmEuY29tL2FwaS92LQ=='), // 'https://mock-api.connectapp.telstra.com/api/v-',
        apiOGWHostUrl: getHostUrl('aHR0cHM6Ly9tb2NrLWFwaS5jb25uZWN0YXBwLm5wLmluLnRlbHN0cmEuY29tLmF1L2FwaS92LWRldmVsb3Blcg=='), // 'https://mock-api.connectapp.np.in.telstra.com.au/api/v-developer',
        appHostUrl: getHostUrl('aHR0cHM6Ly9tb2NrLmNvbm5lY3RhcHAubnAuaW4udGVsc3RyYS5jb20uYXU='), // 'https://mock.connectapp.np.in.telstra.com.au',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    },

    [Environments.Local]: {
        isForDevelopment: true,
        apiHostUrl: getHostUrl('aHR0cDovL2xvY2FsaG9zdDo1MDUwL2FwaS92LQ=='), // 'http://localhost:5050/api/v-',
        apiOGWHostUrl: getHostUrl('aHR0cDovL2xvY2FsaG9zdDo1MDUwL2FwaS92LWRldmVsb3Blcg=='), // 'http://localhost:5050/api/v-developer',
        appHostUrl: getHostUrl('aHR0cHM6Ly9sb2NhbGhvc3Q6NTAwMA=='), // 'https://localhost:5000',
        loadFlagsFromSubscriptions: true,
        loadCcvFlag: true
    }
};
// tslint:enable: max-line-length

const getConfig: () => IConfig = () => {
    const env = getCurrentEnvironment();
    const appConfig = getAppConfig();
    const baseConfig = isEDN ? ConfigurationsEDN[env] : Configurations[env];

    if (env === Environments.Local ||
        env === Environments.Mock) {
        baseConfig.apiHostUrl = `${baseConfig.apiHostUrl}${appConfig.mockApiVersion}`;
        baseConfig.apiOGWHostUrl = `${baseConfig.apiOGWHostUrl}`;
    }

    if (isAuthExemptedForEnvironment() &&
        baseConfig.apiHostAuthExemptUrl) {
        baseConfig.apiHostUrl = `${baseConfig.apiHostAuthExemptUrl}`;
    }

    // Extra (additional) config fields that depend on the `baseConfig` fields above
    const configExtras: IConfigExtras = {
        application: Object.freeze(appConfig),
        auth: Object.freeze(getAuthConfig(baseConfig))
    };

    const composedConfig: IConfig = Object.freeze({
        ...baseConfig,
        ...configExtras,
    });

    return composedConfig;
};

export const getCurrentEnvironment: (() => Environment) = () => {
    if ('env' in global) {
        return global.env as Environment;
    }

    const env = mobileEnvironment() as Environment;
    return env || Environments.QA;
};

export const Config = getConfig();

export const isDev = () => Config.isForDevelopment;
